import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Building2, Calendar, Mail } from "lucide-react";

const PILL = (color) => {
  switch (color) {
    case "red": case "orange": case "amber": return "bg-warn-soft text-warn border-warn/40";
    case "light_green": case "dark_green": return "bg-good-soft text-good border-good/40";
    default: return "bg-secondary text-ink-muted border-ink-line";
  }
};

export default function CompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/companies/${id}`);
        setData(data);
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [id]);

  if (!data) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;

  const c = data.company;
  const org = data.organisation_assessments || {};
  const tenders = data.tender_assessments || [];
  const agg = data.aggregate_metrics || {};

  const filteredTenders = tab === "all" ? tenders : tenders.filter((t) => t[tab]);

  const ScoreCell = ({ info, framework, tenderId }) => {
    if (!info) return <span className="text-ink-muted text-xs">—</span>;
    return (
      <Link to={`/admin/${framework}/${info.submission_id}`} className="flex items-center gap-2 hover:opacity-80">
        <span className="font-num text-sm">{info.score}</span>
        <span className={`chip text-[10px] ${PILL(info.tier_color)}`}>{info.tier_label}</span>
      </Link>
    );
  };

  return (
    <div className="space-y-7" data-testid="company-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Companies
      </button>

      <div className="border-b border-ink-line pb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand text-white grid place-items-center rounded-sm">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Company</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1" data-testid="company-name">{c.name}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-ink-muted mt-2">
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {c.email || "—"}</span>
              <span className="flex items-center gap-1.5 font-num"><Calendar className="w-3.5 h-3.5" /> Last active {c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Org-level assessments */}
      <div className="card-flat p-6" data-testid="company-org-assessments">
        <div className="stat-label">Organisation-Level Assessments</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {[
            { key: "trca", label: "TRCA", info: org.trca },
            { key: "pcab", label: "PCAB", info: org.pcab },
          ].map((row) => (
            <div key={row.key} className="border border-ink-line p-4 rounded-sm">
              <div className="text-[11px] uppercase tracking-wider text-ink-muted">{row.label}</div>
              {row.info ? (
                <>
                  <div className="font-num text-3xl font-bold mt-1">{row.info.score} <span className="text-base text-ink-muted">/100</span></div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className={`chip text-[10px] ${PILL(row.info.tier_color)}`}>{row.info.tier_label}</span>
                    <Link to={`/admin/${row.key}/${row.info.submission_id}`} className="text-xs text-brand hover:text-brand-hover">View detail →</Link>
                  </div>
                  <div className="text-[11px] text-ink-muted mt-2 font-num">{row.info.completed_at ? new Date(row.info.completed_at).toLocaleDateString() : ""}</div>
                </>
              ) : (
                <div className="text-sm text-ink-muted mt-2">Not yet completed</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Aggregate metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="card-flat p-3"><div className="stat-label">Tenders</div><div className="font-num text-2xl font-bold mt-1">{agg.tenders_submitted || 0}</div></div>
        <div className="card-flat p-3"><div className="stat-label">L1 wins</div><div className="font-num text-2xl font-bold mt-1 text-good">{agg.l1_wins || 0}</div></div>
        <div className="card-flat p-3"><div className="stat-label">Avg BNBD</div><div className="font-num text-2xl font-bold mt-1">{agg.avg_bnbd ?? "—"}</div></div>
        <div className="card-flat p-3"><div className="stat-label">Avg PCEI</div><div className="font-num text-2xl font-bold mt-1">{agg.avg_pcei ?? "—"}</div></div>
        <div className="card-flat p-3"><div className="stat-label">Avg TAPI</div><div className="font-num text-2xl font-bold mt-1">{agg.avg_tapi ?? "—"}</div></div>
        <div className="card-flat p-3 col-span-2 md:col-span-1">
          <div className="stat-label">Top root cause</div>
          <div className="text-sm font-medium mt-1 truncate">{agg.most_common_root_cause ? `${agg.most_common_root_cause.cause} (${agg.most_common_root_cause.count}×)` : "—"}</div>
        </div>
      </div>

      {/* Tender-level submissions */}
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="stat-label">Tender-Level Submissions</div>
            <p className="text-sm text-ink-muted mt-1">All tenders this company has run any framework against.</p>
          </div>
          <div className="flex gap-0 border border-ink-line rounded-sm bg-white overflow-hidden">
            {["all", "bnbd", "pcei", "tapi"].map((k, i) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`company-tender-tab-${k}`}
                className={`px-3 py-2 text-[12px] uppercase tracking-wider ${i < 3 ? "border-r border-ink-line" : ""} ${tab === k ? "bg-brand text-white font-medium" : "text-ink-muted hover:bg-secondary"}`}>
                {k}
              </button>
            ))}
          </div>
        </div>

        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary border-b border-ink-line">
              <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
                <th className="px-5 py-3 font-medium">Tender</th>
                <th className="px-5 py-3 font-medium">Deadline</th>
                <th className="px-5 py-3 font-medium">BNBD</th>
                <th className="px-5 py-3 font-medium">PCEI</th>
                <th className="px-5 py-3 font-medium">TAPI</th>
                <th className="px-5 py-3 font-medium">Result</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredTenders.length === 0 && (<tr><td colSpan={7} className="px-5 py-10 text-center text-ink-muted">No tenders match.</td></tr>)}
              {filteredTenders.map((t) => (
                <tr key={t.tender_id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                  <td className="px-5 py-3">
                    <div className="font-medium truncate max-w-md" title={t.tender_title}>{t.tender_title}</div>
                    <div className="text-[11px] text-ink-muted truncate max-w-md">{t.client || ""}</div>
                  </td>
                  <td className="px-5 py-3 text-ink-muted font-num text-xs">{t.closing_date ? new Date(t.closing_date).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-3"><ScoreCell info={t.bnbd} framework="bnbd" tenderId={t.tender_id} /></td>
                  <td className="px-5 py-3"><ScoreCell info={t.pcei} framework="pcei" tenderId={t.tender_id} /></td>
                  <td className="px-5 py-3"><ScoreCell info={t.tapi} framework="tapi" tenderId={t.tender_id} /></td>
                  <td className="px-5 py-3 text-xs">{t.result_position ? <span className="chip text-[10px] bg-secondary text-ink border-ink-line">{t.result_position}</span> : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/tenders/${t.tender_id}`} className="text-xs text-brand hover:text-brand-hover">Open tender →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
